import { createContext, useContext, type PropsWithChildren } from "react";

import type { ScoutInternalEventStatus } from "../hooks/scoutInternalEvents";
import type { ScoutConversationPrivateDocumentRow } from "../hooks/useScoutConversationPrivateDocuments";

import { LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND } from "./scoutLargeFileFullAnalysisProposal";
import { TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND } from "./scoutTenantDocumentProposal";

export type ScoutProposalResponseStatus = ScoutInternalEventStatus;

export type TenantDocumentIngestionProposalHandlers = {
  respondedProposals: Record<string, ScoutProposalResponseStatus>;
  isProcessing: boolean;
  onConfirm: (input: {
    proposalId: string;
    documentIds: string[];
    totalProposedCount: number;
  }) => Promise<void>;
  onDecline: (input: { proposalId: string; totalProposedCount: number }) => Promise<void>;
};

export type LargeFileFullAnalysisProposalHandlers = {
  respondedProposals: Record<string, ScoutProposalResponseStatus>;
  isProcessing: boolean;
  onConfirm: (input: {
    proposalId: string;
    privateDocumentIds: string[];
    documents: ScoutConversationPrivateDocumentRow[];
  }) => Promise<void>;
  onDecline: (input: { proposalId: string; privateDocumentIds: string[] }) => Promise<void>;
};

/**
 * Runtime resolve handlers keyed by proposal kind. Cards / surfaces look up
 * their kind here for confirm/decline + responded-state. Add a key when
 * registering a new kind in `scoutProposalRegistry`.
 */
export type ScoutProposalHandlersByKind = {
  [TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND]: TenantDocumentIngestionProposalHandlers;
  [LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND]: LargeFileFullAnalysisProposalHandlers;
};

export type ScoutProposalContextValue = {
  handlers: ScoutProposalHandlersByKind;
};

const ScoutProposalContext = createContext<ScoutProposalContextValue | undefined>(undefined);

export function ScoutProposalProvider({
  value,
  children,
}: PropsWithChildren<{ value: ScoutProposalContextValue }>) {
  return <ScoutProposalContext.Provider value={value}>{children}</ScoutProposalContext.Provider>;
}

/**
 * Kind-scoped handlers for an approval surface. Returns undefined when
 * rendered outside a provider (buttons stay locked / no-op).
 */
export function useScoutProposalHandlers<K extends keyof ScoutProposalHandlersByKind>(
  kind: K
): ScoutProposalHandlersByKind[K] | undefined {
  return useContext(ScoutProposalContext)?.handlers[kind];
}
